import { NgClass, NgIf } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { SharedService } from 'src/app/services/shared.service';
import { Constants } from 'src/app/shared/constants';
import { RegionNamePipe } from 'src/app/shared/pipes/region-name.pipe';
import { FilterConfig } from 'src/app/types/filter-config';
import { FilterButtonComponent } from '../filter-button/filter-button.component';
import { SearchFieldComponent } from '../search-field/search-field.component';

/**
 * Custom component which serves as a wrapper for multiple filter components
 * default is to show search field only
 */
@Component({
    selector: 'riege-filter-toolbar',
    imports: [
        SearchFieldComponent,
        FilterButtonComponent,
        MatChipsModule,
        MatIconModule,
        NgClass,
        NgIf,
        RegionNamePipe
    ],
    templateUrl: './filter-toolbar.component.html',
    styleUrl: './filter-toolbar.component.scss',
    standalone: true
})
export class FilterToolbarComponent implements OnInit, OnDestroy {
    /** flag, if filter button should be shown */
    @Input() showFilter = false;
    /** flag, if filter settings should be shown */
    @Input() showSettings = false;
    /** required filter config input */
    @Input({ required: true }) filterConfig!: FilterConfig;

    /** reference on search term */
    searchTerm = '';
    /** reference on filter setting as defined in url params */
    filterSettings?: Params;

    /** broadcast search changed */
    @Output() searchChanged = new EventEmitter();
    /** broadcast filter changed */
    @Output() filterChanged = new EventEmitter();

    /** reference on filter counter - how many filters have been set */
    count = 0;

    /** reference on {@link Constants.services} */
    readonly serviceDefs = Constants.services;

    /** @ignore */
    #route = inject(ActivatedRoute);
    #sharedService = inject(SharedService);

    /** reference on selected services - to be shown in filter settings */
    selectedServices: any[] = [];

    constructor() {
        /** change filterConfig when notified by service */
        effect(() => {
            const filters = this.#sharedService.filters();
            this.filterChanged.emit(filters);

            this.countFilters();
        });
    }

    ngOnInit() {
        /** get route query parameters on filters */
        this.#route.params.subscribe((params) => {
            this.setFilterSettings(params);
            this.filterSettings = params;
        });

        // set search term in input
        this.searchTerm = this.filterConfig?.filter.search;
    }

    /**
     * apply query parameters on filters
     * known parameters are:
     * - search {@type string}
     * - countryCode {@type string} - two letter country code
     * - service {@type Service}
     */
    setFilterSettings(params: Params) {
        /** search term */
        if (params['search']) this.filterConfig!.filter.search = params['search'];

        /** country filter */
        if (params['countryCode']) {
            this.filterConfig.filter.countryCode = params['countryCode'];
        }
        /** service filters */
        if (params['service']) {
            const serviceParams = params['service'].split(',');

            const props = {};
            serviceParams.forEach((param: string) => {
                Object.assign(props, { [param]: true });
            });
            Object.assign(this.filterConfig.filter.services, props);

            this.selectedServices = Object.keys(props);
        }
        /** sorting filters */
        if (params['sort']) {
            this.filterConfig.filter.sorting.sort = params['sort'];
        }
        if (params['sortDirection']) {
            this.filterConfig.filter.sorting.sortDirection = params['sortDirection'];
        }

        this.countFilters();
    }

    countFilters() {
        let num = 0;

        /** set num of given service filters */
        if (this.filterConfig.filter.countryCode !== '') num++;
        const numServices = Object.values(this.filterConfig.filter.services).filter((s) => s === true);
        num = num + numServices.length;

        if (this.filterConfig.filter.sorting.sortDirection != '') num++;

        this.count = num;
    }

    /** pass entered search term to service */
    search(term: string) {
        this.searchChanged.emit(term);
    }

    /** helper function to remove active filter */
    removeFilter(filterKey: string) {
        console.log('not implemented yet');
        return;
        // if ( filterKey === 'countryCode' ) {
        //     Object.assign( this.filterConfig.filter, { [filterKey]: '' } );
        // }
        // else {
        //     // this.filterConfig.filter.services. = false;
        // }

        // this.filterChanged.emit( this.filterConfig.filter );
        // this.countFilters();
    }

    /** @ignore */
    ngOnDestroy() {
        this.#sharedService.filters.set(undefined);
    }
}
